import { api, envelope, setAccessToken } from './client';
import type {
  ChangeEmailRequestDto,
  ChangePasswordDto,
  ConfirmEmailChangeDto,
  ConfirmEmailDto,
  CurrentUserDto,
  EnableTwoFactorDto,
  ForgotPasswordDto,
  LoginDto,
  LoginResultDto,
  RegisterDto,
  ResendConfirmationDto,
  ResendTwoFactorCodeDto,
  ResetPasswordDto,
  UpdateProfileDto,
  UserSessionDto,
  VerifyTwoFactorDto,
} from './types';

/* ------------------------------------------------------------------ *
 *  /api/auth — 22 uc
 *
 *  Access token yanit govdesinde gelir ve bellekte tutulur.
 *  Refresh token HttpOnly cerezdedir; bu dosya ona hic dokunmaz.
 * ------------------------------------------------------------------ */

/** Giris sonucunu isler: token varsa saklar, 2FA gerekiyorsa cagirana birakir. */
function absorbToken(result: LoginResultDto): LoginResultDto {
  if (result.token?.accessToken) {
    setAccessToken(result.token.accessToken);
  }
  return result;
}

export const authApi = {
  /** 1. Yeni musteri kaydi. E-posta dogrulama baglantisi gonderilir. */
  register: (dto: RegisterDto) => api.postCommand('/api/auth/register', dto),

  /** 2. E-postadaki baglantiyla hesabi dogrular. */
  confirmEmail: (dto: ConfirmEmailDto) => api.postCommand('/api/auth/confirm-email', dto),

  /** 3. Dogrulama e-postasini yeniden gonderir. */
  resendConfirmation: (dto: ResendConfirmationDto) =>
    api.postCommand('/api/auth/resend-confirmation', dto),

  /** 4. Giris. 3 ardisik hatada hesap kilitlenir (423). */
  login: async (dto: LoginDto) =>
    absorbToken(await api.post<LoginResultDto>('/api/auth/login', dto)),

  /** 5. E-posta ile gelen tek kullanimlik kodu dogrular. */
  verifyTwoFactor: async (dto: VerifyTwoFactorDto) =>
    absorbToken(await api.post<LoginResultDto>('/api/auth/verify-two-factor', dto)),

  /** 6. Iki adimli dogrulama kodunu yeniden gonderir. */
  resendTwoFactorCode: (dto: ResendTwoFactorCodeDto) =>
    api.postCommand('/api/auth/resend-two-factor-code', dto),

  /** 7. Access token'i yeniler; refresh token cerezden okunur ve dondurulur. */
  refresh: async () => {
    const body = await envelope<LoginResultDto>({ method: 'POST', url: '/api/auth/refresh' });
    const token = body.data?.token?.accessToken ?? null;
    setAccessToken(token);
    return body.data;
  },

  /** 8. Yalnizca bu oturumu kapatir. */
  logout: async () => {
    try {
      return await api.postCommand('/api/auth/logout');
    } finally {
      setAccessToken(null);
    }
  },

  /** 9. Kullanicinin tum cihazlardaki oturumlarini kapatir. */
  logoutAll: async () => {
    try {
      return await api.postCommand('/api/auth/logout-all');
    } finally {
      setAccessToken(null);
    }
  },

  /** 10. Parola sifirlama baglantisi ister. */
  forgotPassword: (dto: ForgotPasswordDto) => api.postCommand('/api/auth/forgot-password', dto),

  /** 11. Baglantidaki token ile yeni parolayi belirler. */
  resetPassword: async (dto: ResetPasswordDto) => {
    const message = await api.postCommand('/api/auth/reset-password', dto);
    setAccessToken(null);
    return message;
  },

  /** 12. Giris yapmis kullanicinin parolasini degistirir. */
  changePassword: (dto: ChangePasswordDto) => api.postCommand('/api/auth/change-password', dto),

  /** 13. Oturumdaki kullanicinin profili, rolleri ve izinleri. */
  me: () => api.get<CurrentUserDto>('/api/auth/me'),

  /** 14. Ad, soyad, telefon ve pazarlama izni gunceller. */
  updateProfile: (dto: UpdateProfileDto) => api.putCommand('/api/auth/me', dto),

  /** 15. E-posta degisikligi talebi; yeni adrese dogrulama gonderilir. */
  requestEmailChange: (dto: ChangeEmailRequestDto) =>
    api.postCommand('/api/auth/change-email', dto),

  /** 16. E-posta degisikligini onaylar. */
  confirmEmailChange: (dto: ConfirmEmailChangeDto) =>
    api.postCommand('/api/auth/confirm-email-change', dto),

  /** 17. Iki adimli dogrulamayi acar. */
  enableTwoFactor: (dto: EnableTwoFactorDto) => api.postCommand('/api/auth/two-factor/enable', dto),

  /** 18. Iki adimli dogrulamayi kapatir. */
  disableTwoFactor: (dto: EnableTwoFactorDto) =>
    api.postCommand('/api/auth/two-factor/disable', dto),

  /** 19. Acik oturumlar (cihaz, IP, son kullanim). */
  sessions: () => api.get<UserSessionDto[]>('/api/auth/sessions'),

  /** 20. Secilen oturumu sonlandirir. */
  revokeSession: (sessionId: number) => api.deleteCommand(`/api/auth/sessions/${sessionId}`),

  /** 21. KVKK: kisisel verilerin disa aktarimi. */
  exportPersonalData: () => api.get<Record<string, unknown>>('/api/auth/me/personal-data'),

  /** 22. Hesabi siler; parola dogrulamasi ister. */
  deleteAccount: async (dto: EnableTwoFactorDto) => {
    const message = await api.postCommand('/api/auth/me/delete', dto);
    setAccessToken(null);
    return message;
  },
};
